// Provider-agnostic ad wrapper service
// Supports multiple ad providers with a consistent interface

const adProviders = {
    dummy: {
        name: 'Dummy Provider',
        showRewardedAd(rewardType) {
            // Returns a promise that resolves when ad is "watched"
            // The actual display is handled by AdModal component
            return Promise.resolve({watched: true, rewardType})
        },
        showAd() {
            // Non-rewarded ad display
            return Promise.resolve({watched: true})
        }
    },

    applixir: {
        name: 'Applixir',
        showRewardedAd(rewardType) {
            // Future: Applixir rewarded video integration
            // window.applixir?.showRewardedVideo({ ... })
            console.warn('Applixir provider not implemented')
            return Promise.reject(new Error('Applixir not implemented'))
        },
        showAd() {
            console.warn('Applixir provider not implemented')
            return Promise.reject(new Error('Applixir not implemented'))
        }
    },

    playwire: {
        name: 'Playwire',
        showRewardedAd(rewardType) {
            // Future: Playwire rewarded ad integration
            console.warn('Playwire provider not implemented')
            return Promise.reject(new Error('Playwire not implemented'))
        },
        showAd() {
            console.warn('Playwire provider not implemented')
            return Promise.reject(new Error('Playwire not implemented'))
        }
    }
}

// Reactive state for ad display (used by components)
let adModalCallback = null
let adModalResolve = null
let adModalReject = null

export const adService = {
    currentProvider: 'dummy',

    // Minimum time before skip is allowed (ms)
    SKIP_DELAY: 3000,

    // Get the current provider object
    getProvider() {
        return adProviders[this.currentProvider] || adProviders.dummy
    },

    // Set the active provider
    setProvider(providerName) {
        if (adProviders[providerName]) {
            this.currentProvider = providerName
            return true
        }
        console.warn(`Unknown ad provider: ${providerName}`)
        return false
    },

    // Register callback for showing ad modal (called by App/Game component)
    registerModalCallback(callback) {
        adModalCallback = callback
    },

    // Show a rewarded ad, returns promise that resolves when ad completed
    // rewardType: 'bonus_passes' | 'reveal_answer' | etc.
    showRewardedAd(rewardType) {
        return new Promise((resolve, reject) => {
            if (!adModalCallback) {
                console.error('Ad modal callback not registered')
                reject(new Error('Ad modal not available'))
                return
            }

            adModalResolve = resolve
            adModalReject = reject

            // Trigger the modal display
            adModalCallback({
                type: 'rewarded',
                rewardType,
                onComplete: (result) => {
                    if (adModalResolve) {
                        adModalResolve(result)
                        adModalResolve = null
                        adModalReject = null
                    }
                },
                onSkip: () => {
                    if (adModalResolve) {
                        adModalResolve({watched: true, skipped: true})
                        adModalResolve = null
                        adModalReject = null
                    }
                },
                onClose: () => {
                    if (adModalReject) {
                        adModalReject(new Error('Ad closed without completion'))
                        adModalResolve = null
                        adModalReject = null
                    }
                }
            })
        })
    },

    // Show a non-rewarded ad (just displays, for reveal answers etc.)
    showAd() {
        return new Promise((resolve, reject) => {
            if (!adModalCallback) {
                console.error('Ad modal callback not registered')
                reject(new Error('Ad modal not available'))
                return
            }

            adModalResolve = resolve
            adModalReject = reject

            adModalCallback({
                type: 'standard',
                onComplete: (result) => {
                    if (adModalResolve) {
                        adModalResolve(result)
                        adModalResolve = null
                        adModalReject = null
                    }
                },
                onSkip: () => {
                    if (adModalResolve) {
                        adModalResolve({watched: true, skipped: true})
                        adModalResolve = null
                        adModalReject = null
                    }
                },
                onClose: () => {
                    if (adModalReject) {
                        adModalReject(new Error('Ad closed without completion'))
                        adModalResolve = null
                        adModalReject = null
                    }
                }
            })
        })
    },

    // Check if ads are available
    isAvailable() {
        return this.currentProvider === 'dummy' || !!adProviders[this.currentProvider]
    }
}

export default adService
